import {
  Button,
  Field,
  FormCheckbox,
  FormInput,
  FormTextarea,
} from "@devhop/ui";
import { Plus, Trash2 } from "lucide-react";
import type { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import type { QCFormValues } from "./QCForm";

type QCItemsFieldProps = {
  control: Control<QCFormValues>;
  watch: UseFormWatch<QCFormValues>;
  setValue: UseFormSetValue<QCFormValues>;
};

export function QCItemsField({ control, watch, setValue }: QCItemsFieldProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "items",
  });

  const addItem = () => {
    append({
      title: "",
      description: null,
      orderIndex: fields.length,
      isRequired: true,
      passCriteria: null,
      failCriteria: null,
    });
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    move(fromIndex, toIndex);
    // Update orderIndex values after move
    const currentItems = watch("items");
    currentItems.forEach((_, index) => {
      setValue(`items.${index}.orderIndex`, index);
    });
  };

  return (
    <Field name="items" label="ລາຍການກວດສອບ">
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex gap-2 rounded-lg border bg-card p-4"
          >
            <div className="flex flex-1 flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-muted-foreground text-sm">
                  #{index + 1}
                </span>
              </div>
              <FormInput
                name={`items.${index}.title`}
                placeholder="ຕົວຢ່າງ: ສີສະກຣີນຕ້ອງຕົງຕາມແບບ"
                requiredMark
                className="flex-1"
              />
              <FormTextarea
                name={`items.${index}.description`}
                placeholder="ຄໍາອະທິບາຍ (ບໍ່ບັງຄັບ)"
                className="flex-1"
                rows={2}
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormTextarea
                  name={`items.${index}.passCriteria`}
                  placeholder="ເກນຜ່ານ (ບໍ່ບັງຄັບ)"
                  className="flex-1"
                  rows={2}
                />
                <FormTextarea
                  name={`items.${index}.failCriteria`}
                  placeholder="ເກນບໍ່ຜ່ານ (ບໍ່ບັງຄັບ)"
                  className="flex-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-4">
                <FormCheckbox
                  name={`items.${index}.isRequired`}
                  label="ຈໍາເປັນຕ້ອງກວດສອບ"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {index > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, index - 1)}
                  title="ຍ້າຍຂຶ້ນ"
                >
                  ↑
                </Button>
              )}
              {index < fields.length - 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, index + 1)}
                  title="ຍ້າຍລົງ"
                >
                  ↓
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={addItem}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          ເພີ່ມລາຍການກວດສອບ
        </Button>

        {fields.length === 0 && (
          <p className="text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີລາຍການກວດສອບ. ກົດປຸ່ມ "ເພີ່ມລາຍການກວດສອບ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        )}
      </div>
    </Field>
  );
}
